import { useState } from 'react';
import { isTrial, getTrialDaysRemaining } from '../lib/license';
import { isWebMode } from '../lib/webUsage';
import { AlertTriangle, X } from 'lucide-react';

/**
 * Thin banner at the top of the app when in trial mode (desktop only).
 *   - Active trial: days remaining + "Enter Code" (dismissible).
 *   - Expired trial: persistent amber bar with an "Upgrade" CTA (opens the web
 *     Stripe flow in the browser, via onUpgrade) + "Enter Code". Non-dismissible.
 */
export default function TrialBanner({ onEnterCode, onUpgrade }) {
  const [dismissed, setDismissed] = useState(false);

  if (isWebMode()) return null;
  if (!isTrial() || dismissed) return null;

  const daysLeft = getTrialDaysRemaining();
  const expired = daysLeft === 0;

  return (
    <div
      className={`flex items-center justify-between gap-2 px-4 py-2 text-sm ${
        expired
          ? 'bg-amber-100 text-amber-900 border-b border-amber-200'
          : 'bg-brand-light text-brand-dark border-b border-brand-muted/30'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0">
        {expired && <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />}
        <span className="truncate">
          {expired
            ? 'Your free trial has ended — get a lifetime license to keep every tool. Lite $49 · Pro $70.'
            : `You're using Scrivar in trial mode — ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining.`}
        </span>
      </div>

      <div className="flex items-center gap-2 shrink-0">
        {expired && (
          <button
            onClick={() => onUpgrade?.()}
            className="px-3 py-0.5 rounded text-xs font-semibold bg-amber-600 text-white hover:bg-amber-700"
          >
            Upgrade
          </button>
        )}
        <button
          onClick={() => onEnterCode?.()}
          className={`px-3 py-0.5 rounded text-xs font-semibold ${
            expired
              ? 'bg-white text-amber-800 border border-amber-300 hover:bg-amber-50'
              : 'bg-brand-blue text-white hover:bg-brand-dark'
          }`}
        >
          Enter Code
        </button>
        {!expired && (
          <button
            onClick={() => setDismissed(true)}
            className="text-brand-muted hover:text-brand-blue"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
